import * as React from 'react';
import { CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';

import {
  CustomerTokenContextType,
  CustomerTokenType,
  CustomerTokenContext,
} from 'contexts/CustomerTokenContext';
import {
  clearCognitoOAuth,
  loadCognitoOAuth,
  storeCognitoOAuth,
} from 'lib/manageCognitoOAuthLocalStorage';
import { IdProvider } from 'models/customer';
import axios from 'axios';
import config from 'config';
import { useSelector } from 'react-redux';
import { ReduxState } from 'ducks';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { revokeGoogleOAuthToken } from 'lib/util/googleOAuthRevoke';

export const CustomerTokenProvider = ({ children }: { children: React.ReactNode }) => {
  const [idToken, setIdToken] = React.useState('');
  const [accessToken, setAccessToken] = React.useState('');
  const [refreshToken, setRefreshToken] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState(0);
  const [idProvider, setIdProvider] = React.useState<IdProvider | null>(null);
  const [loading, setLoading] = React.useState(true);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  // Get apiKey from context instead of managing it internally
  const { apiKey } = React.useContext(ApiKeyContext);

  const customerToken = useSelector((state: ReduxState) => state.customer.customerToken);

  const userPool = React.useMemo(() => {
    if (!settings.customer_ledger_settings) {
      return null;
    }
    const cognitoInfo = settings.customer_ledger_settings?.cognito;

    const poolData = {
      UserPoolId: cognitoInfo?.user_pool_id ?? '',
      ClientId: cognitoInfo?.client_id ?? '',
    };

    return new CognitoUserPool(poolData);
  }, [settings]);

  const logout = React.useCallback(async () => {
    console.log(`[CustomerTokenProvider] Starting logout for provider: ${idProvider}`);

    try {
      // Provider-specific logout
      if (idProvider === 'COGNITO') {
        // Use CognitoUserPool for logout
        const currentUser = userPool?.getCurrentUser();
        if (currentUser) {
          currentUser.signOut();
          console.log('[CustomerTokenProvider] Cognito user signOut successful');
        } else {
          console.warn('[CustomerTokenProvider] No current Cognito user found');
        }
      } else if (idProvider === 'GOOGLE' && (accessToken || refreshToken)) {
        try {
          await revokeGoogleOAuthToken(accessToken, refreshToken);
          console.log('[CustomerTokenProvider] Google token revoked successfully');
        } catch (error) {
          console.error('[CustomerTokenProvider] Google token revoke failed:', error);
          // Continue with logout even if revoke fails
        }
      } else if (idProvider === 'FACEBOOK') {
        // Facebook doesn't require specific logout action
        console.log('[CustomerTokenProvider] Facebook logout - no specific action required');
      } else if (idProvider === 'SIGIRA') {
        // SIGIRA doesn't require specific logout action
        console.log('[CustomerTokenProvider] SIGIRA logout - no specific action required');
      }
    } catch (error) {
      console.error('[CustomerTokenProvider] Provider-specific logout failed:', error);
      // Continue with generic cleanup even if provider-specific logout fails
    }

    // Common cleanup for all providers
    clearCognitoOAuth(apiKey);
    setIdToken('');
    setAccessToken('');
    setRefreshToken('');
    setExpiresAt(0);
    setIdProvider(null);

    console.log('[CustomerTokenProvider] Logout completed');
  }, [userPool, apiKey, idProvider, accessToken, refreshToken]);

  const setToken = (token: CustomerTokenType) => {
    // Ensure apiKey is set in the token for storage
    const tokenWithApiKey = { ...token, apiKey };
    storeCognitoOAuth(tokenWithApiKey);
    setIdToken(token.idToken ?? '');
    setAccessToken(token.accessToken ?? '');
    setRefreshToken(token.refreshToken ?? '');
    setExpiresAt(token.expiresAt ?? 0);
    setIdProvider(token.idProvider ?? null);
    setLoading(false);
  };

  const refreshIfNeeded = React.useCallback(() => {
    const buffer = idProvider === 'SIGIRA' ? 1 * 60 * 1000 : 10 * 60 * 1000;
    const now = new Date().getTime();

    if (!expiresAt || expiresAt > now + buffer) return;

    if (idProvider === 'COGNITO') {
      const user = userPool?.getCurrentUser();
      if (!user) return;

      user.getSession((err: any, session: any) => {
        if (err || !session) return logout();
        user.refreshSession(session.getRefreshToken(), (err, newSession) => {
          if (err || !newSession) return logout();
          setToken({
            idToken: newSession.getIdToken().getJwtToken(),
            accessToken: newSession.getAccessToken().getJwtToken(),
            refreshToken: newSession.getRefreshToken().getToken(),
            expiresAt: newSession.getAccessToken().getExpiration() * 1000,
            idProvider: 'COGNITO',
            apiKey,
          });
        });
      });
    } else if (idProvider === 'GOOGLE' && refreshToken) {
      axios
        .get(
          `${config.apiUrl}/customers/-/token?id_provider=${idProvider}&refresh_token=${refreshToken}&grant_type=refresh_token`,
          { headers: { 'x-api-key': apiKey } }
        )
        .then((res) => {
          const tokenData = res.data;
          const expiresAt = tokenData.expiry_date_time_utc
            ? new Date(tokenData.expiry_date_time_utc).getTime()
            : 0;

          setToken({
            idToken: tokenData.id_token,
            accessToken: tokenData.access_token,
            refreshToken: tokenData.refresh_token,
            expiresAt,
            idProvider,
            apiKey,
          });
        })
        .catch((err) => {
          console.error('Refresh failed:', err);
          logout();
        });
    } else if (idProvider === 'FACEBOOK' || idProvider === 'SIGIRA') {
      // if the Provider doesn't have refresh token (Facebook / Sigira)
      logout();
    }
  }, [idProvider, refreshToken, expiresAt, apiKey, logout, userPool]);

  // Load access token from local storage and refresh if needed
  React.useEffect(() => {
    const currentUser = userPool?.getCurrentUser();
    if (currentUser) {
      currentUser.getSession((err: any, session: CognitoUserSession | null) => {
        if (err || !session) {
          logout();
          setLoading(false);
          return;
        }
        setToken({
          idToken: session.getIdToken().getJwtToken(),
          accessToken: session.getAccessToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
          expiresAt: session.getAccessToken().getExpiration() * 1000,
          idProvider: 'COGNITO',
          apiKey,
        });
        refreshIfNeeded();
      });
    } else {
      // Google / Facebook
      loadCognitoOAuth((token) => {
        setToken(token);
        refreshIfNeeded();
      }, apiKey);
      // Set loading to false after attempting to load from localStorage
      setLoading(false);
    }
  }, [apiKey, userPool, refreshIfNeeded, logout]);

  // Interval for refresh token
  React.useEffect(() => {
    const interval = setInterval(() => {
      refreshIfNeeded();
    }, 60_000); // 60 seconds
    return () => clearInterval(interval);
  }, [refreshIfNeeded]);

  // Update token when the token is refreshed by refresh token for Google
  React.useEffect(() => {
    if (customerToken) {
      let expiresAt = 0;
      if (customerToken.expiry_date_time_utc) {
        expiresAt = new Date(customerToken.expiry_date_time_utc).getTime();
      }
      const newToken = {
        idToken: customerToken.id_token ?? '',
        accessToken: customerToken.access_token ?? '',
        refreshToken: customerToken.refresh_token ?? '',
        expiresAt: expiresAt,
        idProvider: customerToken.id_provider ?? null,
        apiKey: customerToken.api_key ?? apiKey,
      };
      setToken(newToken);
    }
  }, [customerToken, apiKey]);

  const value: CustomerTokenContextType = {
    idToken,
    accessToken,
    refreshToken,
    expiresAt,
    idProvider,
    logout,
    setToken,
    apiKey,
    loading,
  };

  return <CustomerTokenContext.Provider value={value}>{children}</CustomerTokenContext.Provider>;
};
